/**
 * Badge Count Utility Functions
 * 
 * Centralized pure functions for calculating badge counts.
 * These use the EXACT same filter logic as previously existed in individual components.
 * 
 * DO NOT modify the filter conditions without updating all consumers.
 */

/**
 * Employee: Count of unviewed PENDING and DISPUTED employment records
 * Used by: EmployeeRecords.js, EmployeeAccess.js, EmployeeDashboard.js, MobileNav.js
 * 
 * Note: Counts both pending and disputed records as these need employee attention.
 * Verified records don't need action, so they're not counted in the badge.
 * 
 * @param {Array} records - Array of employment records
 * @returns {number} Count of unviewed pending/disputed records
 */
export function getEmployeeUnviewedRecordsCount(records = []) {
  return records.filter(r => 
    (r.status === 'pending' || r.status === 'disputed') && r.viewed !== true
  ).length;
}

/**
 * Employee: Count of new profile shares (not viewed by employee)
 * Used by: EmployeeAccess.js, EmployeeRecords.js, NotificationContext.js, MobileNav.js
 * 
 * @param {Array} profileAccess - Array of profile access records
 * @returns {number} Count of new shares
 */
export function getEmployeeNewSharesCount(profileAccess = []) {
  return profileAccess.filter(a => a.viewed_by_employee !== true).length;
}

/**
 * Employer: Count of unviewed pending/disputed verification requests
 * Used by: CompanyDashboardApp.js, NotificationContext.js, MobileNav.js
 * Includes both pending and disputed records as they both need employer attention
 * 
 * @param {Array} pendingVerifications - Array of verification records
 * @returns {number} Count of unviewed pending/disputed requests
 */
export function getEmployerUnviewedPendingCount(pendingVerifications = []) {
  return pendingVerifications.filter(v => 
    (v.status === 'pending' || v.status === 'disputed') && v.viewed !== true
  ).length;
}

/**
 * Employer: Count of unviewed profile access records
 * Used by: CompanyDashboardApp.js, NotificationContext.js, MobileNav.js
 * 
 * @param {Array} profileAccess - Array of profile access records
 * @returns {number} Count of unviewed access records
 */
export function getEmployerUnviewedAccessCount(profileAccess = []) {
  return profileAccess.filter(a => a.viewed !== true).length;
}
